import { GatedQueryError } from "../components/GatedQueryError";
import {
  Banner,
  BlockStack,
  Box,
  Card,
  DataTable,
  Layout,
  Page,
  Select,
  SkeletonBodyText,
  Text,
} from "@shopify/polaris";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  CASHFLOW_DEFAULT_HORIZON_WEEKS,
  CASHFLOW_HORIZON_WEEK_OPTIONS,
  getCashflowCalendar,
} from "../api/cashflow";
import { listLocations } from "../api/locations";
import { toLocationSelectOptions } from "../utils/locationOptions";
import { getMe } from "../api/me";
import { getPlanPreview } from "../api/plan_previews";
import { PlanDataPreviewGate } from "../components/PlanDataPreviewGate";
import { UpgradeNote } from "../components/PlanGate";
import { formatJpDate } from "../utils/invoiceTransition";
import { formatNumber, money } from "../utils/money";
import { useCurrency } from "../lib/useCurrency";
import { useLocationLimit } from "../lib/useLocationLimit";

import { t } from "../lib/i18n";

const FEATURE_NAME = "資金繰りカレンダー";

/** 複数ロケ店とみなす拠点数。1拠点なら絞る余地が無いのでセレクタを出さない。 */
const MULTI_LOCATION_MIN_COUNT = 2;

function CashflowCalendarContent() {
  const currency = useCurrency();
  const yen = (amount: string | number) => money(amount, currency);
  const navigate = useNavigate();
  // 🔴 拠点は「機能」ではなく「数の階段」＝上限に収まっていれば全プランで使える。
  const locationLimit = useLocationLimit();
  const canMultiLocation =
    locationLimit === undefined || locationLimit.limit === null || locationLimit.limit > 1;

  const [weeks, setWeeks] = useState(CASHFLOW_DEFAULT_HORIZON_WEEKS);
  // 配送先（ロケーション）。null = 未選択（バックエンドが主拠点へ解決する）。
  const [locationId, setLocationId] = useState<string | null>(null);

  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const locationCount = meQuery.data?.locations?.count ?? 0;
  const primaryLocationId = meQuery.data?.locations?.primary_location_id ?? null;
  const primaryLocationName = meQuery.data?.locations?.primary_name ?? null;
  const isMultiLocation = locationCount >= MULTI_LOCATION_MIN_COUNT;

  const locationsQuery = useQuery({
    queryKey: ["cashflow-locations"],
    queryFn: listLocations,
    // /api/locations は Business ゲート（403）。未満では取得しない。
    enabled: canMultiLocation && isMultiLocation,
  });

  // 拠点指定はBusinessでしか通らない。未満では null（＝主拠点へバックエンドが解決）に倒す。
  const effectiveLocationId = canMultiLocation ? locationId : null;

  const calendarQuery = useQuery({
    queryKey: ["cashflow-calendar", weeks, effectiveLocationId],
    queryFn: () => getCashflowCalendar(weeks, effectiveLocationId),
    placeholderData: keepPreviousData,
  });

  const locationOptions = canMultiLocation
    ? toLocationSelectOptions(locationsQuery.data ?? [])
    : primaryLocationId !== null && primaryLocationName !== null
      ? [{ label: primaryLocationName, value: String(primaryLocationId) }]
      : [];

  const selectedLocationValue =
    locationId !== null
      ? String(locationId)
      : primaryLocationId !== null
        ? String(primaryLocationId)
        : "";

  const calendar = calendarQuery.data;
  const buckets = calendar?.buckets ?? [];

  const rows = buckets.map((bucket) => [
    t("{date}の週〜", { date: formatJpDate(bucket.week_start) }),
    yen(bucket.amount),
    formatNumber(bucket.supplier_count),
    formatNumber(bucket.line_count),
  ]);

  return (
    <Page
      title={t(FEATURE_NAME)}
      subtitle={t("発注が必要な仕入を入荷予定日で投影し、今後いくら現金が要るかを週次で見える化します")}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="300">
              <Box maxWidth="200px">
                <Select
                  label={t("見通し期間")}
                  options={CASHFLOW_HORIZON_WEEK_OPTIONS.map((w) => ({
                    label: t("{n}週間", { n: w }),
                    value: String(w),
                  }))}
                  value={String(weeks)}
                  onChange={(value) => setWeeks(Number(value))}
                />
              </Box>
              {isMultiLocation && (
                <Box maxWidth="280px">
                  <Select
                    label={t("拠点")}
                    options={locationOptions}
                    value={selectedLocationValue}
                    onChange={(value) => setLocationId(value)}
                    disabled={!canMultiLocation}
                  />
                </Box>
              )}
              {isMultiLocation && !canMultiLocation && (
                <UpgradeNote feature="inventory_transfer">
                  <p>
                    {t("現在は主拠点（{name}）のみの資金繰りカレンダーです。", {
                      name: primaryLocationName ?? t("既定の配送先"),
                    })}
                    {t("他の拠点も含めるには、拠点数の多いプランへの変更が必要です。")}
                  </p>
                </UpgradeNote>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {calendarQuery.isError && (
          <Layout.Section>
            <GatedQueryError
              title={t("資金繰りカレンダーの取得に失敗しました")}
              onRetry={() => void calendarQuery.refetch()}
            />
          </Layout.Section>
        )}

        {calendarQuery.isLoading && (
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        )}

        {calendar && (
          <>
            <Layout.Section>
              <Card>
                <BlockStack gap="200">
                  <Text as="span" variant="bodySm" tone="subdued">
                    {t("今後{weeks}週間で必要な仕入現金の合計", {
                      weeks: calendar.horizon_weeks,
                    })}
                  </Text>
                  <Text as="p" variant="heading2xl">
                    {yen(calendar.total_amount)}
                  </Text>
                  {Number(calendar.beyond_horizon_amount) > 0 && (
                    <Text as="span" variant="bodySm" tone="subdued">
                      {t("この期間より先（リードタイムが長い分）に")}
                      {yen(calendar.beyond_horizon_amount)}
                      {t("の支払いが見込まれます。")}
                    </Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              {rows.length === 0 ? (
                // 原因候補は2つ（発注点・仕入先の割当）なので、行き先も両方出す。
                // 説明だけで手が止まるのは実測済み（2026-07-30・注文書の空状態）。
                <Banner
                  tone="success"
                  title={t("今後の発注に必要な現金はありません")}
                  action={{
                    content: t("発注点を設定する"),
                    onAction: () => navigate("/reorder-policies"),
                  }}
                  secondaryAction={{
                    content: t("仕入先を割り当てる"),
                    onAction: () => navigate("/purchase-orders"),
                  }}
                >
                  <p>
                    {t("発注点を割った商品が無いか、仕入先の割当・原価が未設定の可能性があります。")}
                  </p>
                </Banner>
              ) : (
                <Card padding="0">
                  <DataTable
                    columnContentTypes={["text", "numeric", "numeric", "numeric"]}
                    headings={[t("支払い週"), t("必要現金"), t("仕入先数"), t("明細数")]}
                    rows={rows}
                  />
                </Card>
              )}
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}

export function CashflowCalendar() {
  return (
    <PlanDataPreviewGate
      feature="auto_draft"
      featureName={t(FEATURE_NAME)}
      previewQueryKey={["plan-preview", "cashflow-inputs"]}
      previewQueryFn={async () => [await getPlanPreview("cashflow_inputs")]}
      sections={[
        {
          dataset: "cashflow_inputs",
          title: t("資金繰り計算の対象商品"),
          columns: [
            { key: "product", label: t("商品") },
            { key: "sku", label: "SKU" },
            { key: "supplier", label: t("仕入先") },
            { key: "reorder_point", label: t("発注点") },
            { key: "lead_time", label: t("リードタイム") },
            { key: "unit_cost", label: t("仕入単価") },
          ],
        },
      ]}
    >
      <CashflowCalendarContent />
    </PlanDataPreviewGate>
  );
}
